import type {
  LoadedTile,
  RuntimeTileSource,
  TileLoadRequest,
} from "../world/streaming/tileStreamingController.ts";
import type { RuntimeWorldTilePayloadV2 } from "../world/schema/runtimeTile.ts";
import {
  PREPARE_RUNTIME_TILE_REQUEST,
  RUNTIME_TILE_ERROR_RESPONSE,
  RUNTIME_TILE_READY_RESPONSE,
  type PrepareRuntimeTileRequest,
  type RuntimeTileWorkerResponse,
} from "./tileProtocol.ts";

interface WorkerMessageEvent {
  data: RuntimeTileWorkerResponse;
}

export interface RuntimeTileWorkerPort {
  postMessage(message: PrepareRuntimeTileRequest): void;
  addEventListener(
    type: "message",
    listener: (event: WorkerMessageEvent) => void,
  ): void;
  addEventListener(type: "error", listener: (event: Event) => void): void;
  terminate(): void;
}

export interface BrowserRuntimeTileSourceOptions {
  worker?: RuntimeTileWorkerPort;
  fetch?: typeof globalThis.fetch;
}

interface PendingWorkerRequest {
  tileId: string;
  lod: number;
  resolve(value: LoadedTile): void;
  reject(error: unknown): void;
  removeAbortListener(): void;
}

function createAbortError(): DOMException {
  return new DOMException("Tile load aborted", "AbortError");
}

function hex(bytes: ArrayBuffer): string {
  return [...new Uint8Array(bytes)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

async function verifyManifestIntegrity(
  bytes: Uint8Array<ArrayBuffer>,
  expectedByteLength: number,
  expectedSha256: string,
): Promise<void> {
  if (bytes.byteLength !== expectedByteLength) {
    throw new Error(
      `Runtime tile byte length mismatch: expected ${expectedByteLength}, received ${bytes.byteLength}`,
    );
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  const actualSha256 = hex(digest);
  if (actualSha256 !== expectedSha256) {
    throw new Error(
      `Runtime tile SHA-256 mismatch: expected ${expectedSha256}, received ${actualSha256}`,
    );
  }
}

/**
 * Browser bridge for the framework-free streaming controller. Fetching and
 * worker ownership stay here, outside the core loader.
 */
export class BrowserRuntimeTileSource implements RuntimeTileSource {
  readonly #worker: RuntimeTileWorkerPort;
  readonly #fetch: typeof globalThis.fetch;
  readonly #pending = new Map<string, PendingWorkerRequest>();
  #nextRequestId = 0;
  #disposed = false;

  constructor(options: Readonly<BrowserRuntimeTileSourceOptions> = {}) {
    this.#worker =
      options.worker ??
      (new Worker(new URL("./tileWorker.ts", import.meta.url), {
        type: "module",
      }) as RuntimeTileWorkerPort);
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.#worker.addEventListener("message", (event) => {
      this.#handleWorkerMessage(event.data);
    });
    this.#worker.addEventListener("error", (event) => {
      const error = new Error(
        `Runtime tile worker failed: ${event.type || "unknown error"}`,
      );
      this.#rejectAll(error);
    });
  }

  async load(request: Readonly<TileLoadRequest>): Promise<LoadedTile> {
    if (this.#disposed) {
      throw new Error("BrowserRuntimeTileSource has been disposed");
    }
    if (request.signal.aborted) {
      throw createAbortError();
    }
    const response = await this.#fetch(request.entry.url, {
      signal: request.signal,
    });
    if (!response.ok) {
      throw new Error(
        `Runtime tile fetch failed (${response.status}) for ${request.entry.id}`,
      );
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (request.signal.aborted) {
      throw createAbortError();
    }
    await verifyManifestIntegrity(
      bytes,
      request.entry.byteLength,
      request.entry.sha256,
    );
    if (request.signal.aborted) {
      throw createAbortError();
    }
    const json = new TextDecoder().decode(bytes);
    const requestId = `runtime-tile-${++this.#nextRequestId}`;

    return new Promise<LoadedTile>((resolveLoad, rejectLoad) => {
      const abort = () => {
        const pending = this.#pending.get(requestId);
        if (pending === undefined) {
          return;
        }
        this.#pending.delete(requestId);
        pending.removeAbortListener();
        rejectLoad(createAbortError());
      };
      request.signal.addEventListener("abort", abort, { once: true });
      this.#pending.set(requestId, {
        tileId: request.entry.id,
        lod: request.lod,
        resolve: resolveLoad,
        reject: rejectLoad,
        removeAbortListener: () => {
          request.signal.removeEventListener("abort", abort);
        },
      });
      if (request.signal.aborted) {
        abort();
        return;
      }
      try {
        this.#worker.postMessage({
          type: PREPARE_RUNTIME_TILE_REQUEST,
          requestId,
          tileId: request.entry.id,
          lod: request.lod,
          json,
        });
      } catch (error: unknown) {
        this.#pending.delete(requestId);
        request.signal.removeEventListener("abort", abort);
        rejectLoad(error);
      }
    });
  }

  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#worker.terminate();
    this.#rejectAll(new Error("BrowserRuntimeTileSource has been disposed"));
  }

  #handleWorkerMessage(response: RuntimeTileWorkerResponse): void {
    const pending = this.#pending.get(response.requestId);
    if (pending === undefined) {
      return;
    }
    if (response.type === RUNTIME_TILE_ERROR_RESPONSE) {
      this.#pending.delete(response.requestId);
      pending.removeAbortListener();
      pending.reject(new Error(response.message));
      return;
    }
    if (
      response.type !== RUNTIME_TILE_READY_RESPONSE ||
      response.tileId !== pending.tileId ||
      response.lod !== pending.lod
    ) {
      this.#pending.delete(response.requestId);
      pending.removeAbortListener();
      pending.reject(
        new Error(`Mismatched runtime tile worker response for ${pending.tileId}`),
      );
      return;
    }
    // The worker has already parsed and fully validated this exact,
    // integrity-checked string. Reparse it into the main-thread object graph
    // without walking every coordinate a second time.
    const payload = JSON.parse(response.json) as RuntimeWorldTilePayloadV2;
    // Promise continuations in the streaming controller perform cache
    // transitions and renderer ownership updates. Put those in a fresh task
    // instead of extending the worker-message task that materialized JSON.
    globalThis.setTimeout(() => {
      if (this.#pending.get(response.requestId) !== pending) {
        return;
      }
      this.#pending.delete(response.requestId);
      pending.removeAbortListener();
      pending.resolve({
        payload,
        sizeBytes: response.sourceByteLength,
        instances: response.instances,
      });
    }, 0);
  }

  #rejectAll(error: unknown): void {
    for (const pending of this.#pending.values()) {
      pending.removeAbortListener();
      pending.reject(error);
    }
    this.#pending.clear();
  }
}

export function createBrowserRuntimeTileSource(
  options: Readonly<BrowserRuntimeTileSourceOptions> = {},
): BrowserRuntimeTileSource {
  return new BrowserRuntimeTileSource(options);
}
